import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import VehicleFamilyCard from "./VehicleFamilyCard";
import { VEHICLE_FAMILIES, type VehicleFamily } from "@/data/vehicleFamilies";
import { BrandFragment } from "./FleetMeatSection";
import { loadTripDates, saveTripDates, getSavedDates, todayYmd, addDaysYmd, isAfterYmd, daysBetweenYmd, areDatesValid } from "@/lib/tripDates";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";


interface Props {
  city?: string;
  accent?: string;
  cardBg?: string;
  textClass?: string;
  labelClass?: string;
  dark?: boolean;
  /** When set, cards render a "Select This Van" button that invokes this callback. */
  onSelectFamily?: (family: VehicleFamily) => void;
}

const VISIBLE_SLUGS = new Set([
  "mercedes-sprinter",
  "mercedes-sprinter-executive",
  "mercedes-metris",
  "ford-transit-low",
  "ford-transit",
]);

type SeatFilter = "all" | 8 | 10 | 12 | 15;
type PriceFilter = "any" | 200 | 275 | 400;
type LuggageFilter = "all" | 12 | 15;

const parsePrice = (p?: string) => (p ? parseInt(p.replace(/[^\d]/g, ""), 10) || 0 : 0);

export default function FleetGrid({
  city,
  accent = "#F97316",
  cardBg,
  textClass,
  labelClass = "text-[#1a1a1a]",
  dark,
  onSelectFamily,
}: Props) {
  const isDark = dark ?? (cardBg?.includes("white/5") || labelClass?.includes("text-[#0F172A]"));

  const [seats, setSeats] = useState<SeatFilter>("all");
  const [price, setPrice] = useState<PriceFilter>("any");
  const [luggage, setLuggage] = useState<LuggageFilter>("all");

  const [pickupDate, setPickupDate] = useState<string>("");
  const [returnDate, setReturnDate] = useState<string>("");
  const [pickupTime, setPickupTime] = useState<string>("");
  const [returnTime, setReturnTime] = useState<string>("");

  // Hydrate from the shared trip-date store on mount AND when it changes
  // elsewhere (configure sheet, reserve page, another open tab).
  useEffect(() => {
    const sync = () => {
      const t = getSavedDates();
      if (t.pickupDate) setPickupDate(t.pickupDate);
      if (t.returnDate) setReturnDate(t.returnDate);
      if (t.pickupTime) setPickupTime(t.pickupTime);
      if (t.returnTime) setReturnTime(t.returnTime);
    };
    sync();
    window.addEventListener("tripdates:change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("tripdates:change", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  // Persist as the user types so /configure and /reserve pick them up.
  useEffect(() => {
    saveTripDates({ pickupDate, returnDate, pickupTime, returnTime });
  }, [pickupDate, returnDate, pickupTime, returnTime]);

  // Clear an invalid return when pickup changes.
  useEffect(() => {
    if (pickupDate && returnDate && !isAfterYmd(returnDate, pickupDate)) {
      setReturnDate("");
    }
  }, [pickupDate, returnDate]);

  const today = todayYmd();
  const tripDays = daysBetweenYmd(pickupDate, returnDate);
  const datesValid = areDatesValid(pickupDate, returnDate);
  const dateError = pickupDate && returnDate && !datesValid
    ? "Return date must be after pickup date."
    : null;

  const all = useMemo(() => VEHICLE_FAMILIES.filter((f) => VISIBLE_SLUGS.has(f.slug)), []);

  const visible = useMemo(() => {
    return all.filter((f) => {
      if (seats !== "all" && !f.passengerOptions.includes(seats as number)) return false;
      if (price !== "any") {
        const p = parsePrice(f.startingPrice);
        if (!p || p >= (price as number)) return false;
      }
      if (luggage !== "all" && !f.passengerOptions.includes(luggage as number)) return false;
      return true;
    });
  }, [all, seats, price, luggage]);

  const sprinters = visible.filter((f) => f.slug.startsWith("mercedes"));
  const fords = visible.filter((f) => f.slug.startsWith("ford"));
  const chevys = visible.filter((f) => f.slug.startsWith("chevy"));

  const panelBg = isDark ? "bg-white/5 border-[#0F172A]/8" : "bg-black/[0.02] border-black/10";
  const groupLabel = isDark ? "text-[#0F172A]/60" : "text-black/55";
  const chipIdle = isDark
    ? "bg-white/5 text-[#0F172A]/80 border-[#0F172A]/10 hover:bg-[#0F172A]/6"
    : "bg-white text-black/75 border-black/10 hover:bg-black/5";
  const countText = isDark ? "text-[#0F172A]/70" : "text-black/65";

  const Chip = ({
    active,
    onClick,
    children,
  }: {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`font-display font-bold text-[11px] uppercase tracking-widest px-4 py-2 rounded-full border transition ${
        active ? "text-[#0F172A] border-transparent" : chipIdle
      }`}
      style={active ? { backgroundColor: accent } : undefined}
    >
      {children}
    </button>
  );

  const Row = ({
    label,
    children,
  }: {
    label: string;
    children: React.ReactNode;
  }) => (
    <div>
      <p className={`font-display font-bold text-[10px] uppercase tracking-[0.3em] mb-2 ${groupLabel}`}>
        {label}
      </p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );

  const SectionHeader = ({ title, brand }: { title: string; brand: "Mercedes Sprinter" | "Ford Transit" | "Chevrolet Express" }) => (
    <div className="flex flex-col md:flex-row md:items-center gap-4 mb-5">
      <p
        className="font-display font-extrabold text-3xl md:text-4xl tracking-tight whitespace-nowrap"
        style={{ color: accent }}
      >
        {title}
      </p>
      
    </div>
  );

  const renderGroup = (items: VehicleFamily[]) => (
    <div className="grid md:grid-cols-2 gap-7">
      {items.map((f) => (
        <VehicleFamilyCard
          key={f.slug}
          family={f}
          city={city}
          accent={accent}
          cardBg={cardBg}
          textClass={textClass}
          onSelectFamily={onSelectFamily}
        />
      ))}
    </div>
  );

  const filterRef = useRef<HTMLDivElement | null>(null);
  const [showFab, setShowFab] = useState(false);

  // Show the floating calendar FAB once the filter panel is scrolled out of view.
  useEffect(() => {
    const el = filterRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setShowFab(!entry.isIntersecting),
      { rootMargin: "-80px 0px 0px 0px", threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const scrollToFilter = () => {
    const el = document.getElementById("fleet-filter-panel");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      const input = el.querySelector<HTMLInputElement>('input[type="date"]');
      input?.focus();
    }, 350);
  };

  const datesSelected = Boolean(pickupDate && returnDate);

  return (
    <>
      {/* FILTER PANEL */}
      <div
        ref={filterRef}
        id="fleet-filter-panel"
        className={`mb-10 border rounded-2xl p-5 md:p-6 space-y-5 ${panelBg}`}
      >
        <Row label="Passengers">

          <Chip active={seats === "all"} onClick={() => setSeats("all")}>All seats</Chip>
          {([8, 10, 12, 15] as const).map((n) => (
            <Chip key={n} active={seats === n} onClick={() => setSeats(n)}>{n}</Chip>
          ))}
        </Row>
        <Row label="Price / day">
          <Chip active={price === "any"} onClick={() => setPrice("any")}>Any price</Chip>
          {([200, 275, 400] as const).map((n) => (
            <Chip key={n} active={price === n} onClick={() => setPrice(n)}>Under ${n}</Chip>
          ))}
        </Row>
        <Row label="Luggage">
          <Chip active={luggage === "all"} onClick={() => setLuggage("all")}>All</Chip>
          {([12, 15] as const).map((n) => (
            <Chip key={n} active={luggage === n} onClick={() => setLuggage(n)}>
              Fits {n} luggage
            </Chip>
          ))}
        </Row>
        <Row label="Trip Dates & Times">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 w-full">
            <DatePickerField
              label="Pickup Date"
              value={pickupDate}
              minDate={today}
              onChange={setPickupDate}
              isDark={isDark}
              groupLabel={groupLabel}
            />
            <label className="flex flex-col">
              <span className={`text-[9px] uppercase tracking-widest font-display font-bold mb-1 ${groupLabel}`}>Pickup Time</span>
              <input
                type="time"
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                className={`rounded-full border px-3 py-2 text-xs font-display font-bold ${isDark ? "bg-white/5 text-[#0F172A] border-[#0F172A]/10" : "bg-white text-black/80 border-black/10"}`}
              />
            </label>
            <DatePickerField
              label="Return Date"
              value={returnDate}
              minDate={pickupDate ? addDaysYmd(pickupDate, 1) : today}
              onChange={(v) => {
                if (v && pickupDate && !isAfterYmd(v, pickupDate)) return;
                setReturnDate(v);
              }}
              isDark={isDark}
              groupLabel={groupLabel}
            />
            <label className="flex flex-col">
              <span className={`text-[9px] uppercase tracking-widest font-display font-bold mb-1 ${groupLabel}`}>Return Time</span>
              <input
                type="time"
                value={returnTime}
                onChange={(e) => setReturnTime(e.target.value)}
                className={`rounded-full border px-3 py-2 text-xs font-display font-bold ${isDark ? "bg-white/5 text-[#0F172A] border-[#0F172A]/10" : "bg-white text-black/80 border-black/10"}`}
              />
            </label>
          </div>
          {dateError && (
            <p className="mt-2 text-xs font-display font-bold text-red-500">{dateError}</p>
          )}
        </Row>
        <p className={`text-sm ${countText}`}>
          Showing <span className="font-bold" style={{ color: accent }}>{visible.length}</span> of {all.length} vans
          {tripDays > 0 && (
            <> · <span className="font-bold" style={{ color: accent }}>{tripDays}</span> {tripDays === 1 ? "day" : "days"} selected</>
          )}
        </p>
      </div>

      {visible.length === 0 ? (
        <div className={`border rounded-2xl p-10 text-center ${panelBg}`}>
          <p className={`font-display font-bold text-lg ${isDark ? "text-[#0F172A]" : "text-black"}`}>
            No vans match these filters.
          </p>
          <button
            onClick={() => {
              setSeats("all");
              setPrice("any");
              setLuggage("all");
            }}
            className="mt-3 font-display font-bold text-xs uppercase tracking-widest underline"
            style={{ color: accent }}
          >
            Reset filters
          </button>
        </div>
      ) : (
        <>
          {sprinters.length > 0 && (
            <div>
              <SectionHeader title="Sprinters" brand="Mercedes Sprinter" />
              {renderGroup(sprinters)}
            </div>
          )}

          {fords.length > 0 && (
            <div className={sprinters.length > 0 ? "mt-14" : ""}>
              <SectionHeader title="Ford Transit" brand="Ford Transit" />
              {renderGroup(fords)}
            </div>
          )}

          {chevys.length > 0 && (
            <div className={sprinters.length + fords.length > 0 ? "mt-14" : ""}>
              <SectionHeader title="Chevrolet Express" brand="Chevrolet Express" />
              {renderGroup(chevys)}
            </div>
          )}
        </>
      )}

      {/* Floating calendar FAB — opens the filter and focuses the date input. */}
      {showFab && (
        <button
          type="button"
          onClick={scrollToFilter}
          aria-label="Pick trip dates"
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full text-[#0F172A] font-display font-bold text-xs uppercase tracking-widest shadow-2xl hover:opacity-90 transition"
          style={{ backgroundColor: accent }}
        >
          <CalendarIcon size={16} />
          {datesSelected
            ? `${tripDays} ${tripDays === 1 ? "day" : "days"} · edit`
            : "Pick trip dates"}
        </button>
      )}
    </>
  );
}

function DatePickerField({
  label,
  value,
  minDate,
  onChange,
  isDark,
  groupLabel,
}: {
  label: string;
  value: string;
  minDate: string;
  onChange: (v: string) => void;
  isDark: boolean;
  groupLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const parsed = value ? new Date(`${value}T00:00:00`) : undefined;
  const min = minDate ? new Date(`${minDate}T00:00:00`) : undefined;
  return (
    <div className="flex flex-col">
      <span className={`text-[9px] uppercase tracking-widest font-display font-bold mb-1 ${groupLabel}`}>{label}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "rounded-full border px-3 py-2 text-xs font-display font-bold text-left flex items-center gap-2",
              isDark ? "bg-white/5 text-[#0F172A] border-[#0F172A]/10" : "bg-white text-black/80 border-black/10",
            )}
          >
            <CalendarIcon size={12} className="opacity-70" />
            {parsed ? format(parsed, "MMM d, yyyy") : <span className="opacity-50">Select date</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-50" align="start">
          <Calendar
            mode="single"
            selected={parsed}
            defaultMonth={parsed ?? min}
            onSelect={(d) => {
              if (d) {
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                onChange(`${y}-${m}-${day}`);
                setOpen(false);
              }
            }}
            disabled={min ? { before: min } : undefined}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}


